import { Divider, Form, InputNumber, Radio, Select, Switch, Tag, TimePicker } from 'antd';
import FormField from 'components/FormField';
import dayjs from 'dayjs';
import useSmartField from 'hooks/useSmartField';
import { ReactNode, useCallback } from 'react';
import {
  AiModelState,
  defaultValueMap,
  getPrefixName,
  hideRadioClassName,
  Inline,
  Prefix,
  stores2storeScopes,
  UnConfigStore,
} from './share';
import { updateShippingDays } from 'views/BootGuide/service';
import NormalMode from './NormalMode';
import { T, useTranslate } from '@tolgee/react';
import TransComponent from 'components/TransComponent';
import { useDayTypeSelectOptions } from '@knowledge/knowledges/general/KnowledgeListForm/LogisticsSendOutTime';
import styled from '@emotion/styled';
import useLLM from 'views/BootGuide/hooks/useLLM';

type FV = BootGuide.AiModel.FormValues.ShippingDays;

function ShippingDays({ prefix, singleMode }: { prefix?: Prefix; singleMode?: boolean }) {
  const { t } = useTranslate();

  const LLM = useLLM();

  const {
    field: { value: valueType, onChange: _onValueTypeChange },
  } = useSmartField<FV['valueType']>(getPrefixName('valueType', prefix));
  const onValueTypeChange = (valueType: FV['valueType']) => {
    _onValueTypeChange(valueType);
    setTimeout(() => {
      _onValueTypeChange(valueType);
    });
  };
  const {
    field: { value: storeScopes },
  } = useSmartField<FV['storeScopes']>(getPrefixName('storeScopes', prefix));
  const {
    field: { value: saleType },
  } = useSmartField<FV['saleType']>(getPrefixName('saleType', prefix));

  const unitOptions = useDayTypeSelectOptions();

  const getHideStyle = (type: FV['valueType']) =>
    singleMode && valueType !== type ? { display: 'none' } : undefined;

  return (
    <div>
      <FormField<FV['underMax']>
        name={getPrefixName('underMax', prefix)}
        style={getHideStyle('underMax')}
        validate={({ max }) => {
          if (!max && max !== 0) {
            return t('trade.请输入');
          }
        }}
      >
        {({ field: { value, onChange, onBlur } }) => {
          return (
            <Inline>
              {!singleMode && (
                <Radio
                  checked={valueType === 'underMax'}
                  onChange={({ target: { checked } }) => checked && onValueTypeChange('underMax')}
                />
              )}
              <T
                keyName="knowledge.xDay以内"
                params={{
                  x: (
                    <TransComponent
                      content={
                        <InputNumber
                          precision={0}
                          min={0}
                          value={value.max}
                          onChange={(max: any) => {
                            onChange({
                              ...value,
                              max,
                            });
                          }}
                          onBlur={onBlur}
                        />
                      }
                    />
                  ),
                  day: (
                    <TransComponent
                      content={
                        <Select
                          dropdownMatchSelectWidth={false}
                          options={unitOptions}
                          value={value.unit}
                          onChange={(unit) =>
                            onChange({
                              ...value,
                              unit,
                            })
                          }
                        />
                      }
                    />
                  ),
                }}
              />
            </Inline>
          );
        }}
      </FormField>
      <FormField<FV['range']>
        name={getPrefixName('range', prefix)}
        style={getHideStyle('range')}
        validate={({ rangeMin, rangeMax }) => {
          if ((!rangeMin && rangeMin !== 0) || (!rangeMax && rangeMax !== 0)) {
            return t('trade.请输入');
          }
        }}
      >
        {({ field: { value, onChange, onBlur } }) => {
          return (
            <Inline onBlur={onBlur}>
              {!singleMode && (
                <Radio
                  checked={valueType === 'range'}
                  onChange={({ target: { checked } }) => checked && onValueTypeChange('range')}
                />
              )}
              <T
                keyName="knowledge.商品发货天数range"
                params={{
                  x: (
                    <TransComponent
                      content={
                        <InputNumber
                          precision={0}
                          min={0}
                          max={value.rangeMax ? value.rangeMax - 1 : undefined}
                          value={value.rangeMin}
                          onChange={(rangeMin: any) => {
                            onChange({
                              ...value,
                              rangeMin,
                            });
                          }}
                        />
                      }
                    />
                  ),
                  y: (
                    <TransComponent
                      content={
                        <InputNumber
                          precision={0}
                          min={value.rangeMin ? value.rangeMin + 1 : 1}
                          value={value.rangeMax}
                          onChange={(rangeMax: any) => {
                            onChange({
                              ...value,
                              rangeMax,
                            });
                          }}
                        />
                      }
                    />
                  ),
                  day: (
                    <TransComponent
                      content={
                        <Select
                          dropdownMatchSelectWidth={false}
                          options={unitOptions}
                          value={value.unit}
                          onChange={(unit) =>
                            onChange({
                              ...value,
                              unit,
                            })
                          }
                        />
                      }
                    />
                  ),
                }}
              />
            </Inline>
          );
        }}
      </FormField>
      {(saleType !== 'preSale' || !LLM) && (
        <FormField<FV['todayOrTomorrowTime']>
          name={getPrefixName('todayOrTomorrowTime', prefix)}
          validate={(time) =>
            storeScopes.length && valueType === 'time' && !time
              ? t('department.请选择x', { x: t('Entities.时间') })
              : undefined
          }
          style={getHideStyle('time')}
        >
          {({ field: { value, onChange, onBlur } }) => {
            const picker = (
              <TimePicker
                showSecond={false}
                format="HH:mm"
                placeholder=""
                value={value ? (dayjs(value, 'HH:mm') as any) : undefined}
                onChange={(_, value) => onChange(value)}
                onBlur={onBlur}
              />
            );
            return (
              <Radio
                className={singleMode ? hideRadioClassName : ''}
                checked={valueType === 'time'}
                onChange={({ target: { checked } }) => checked && onValueTypeChange('time')}
              >
                <Inline>
                  <T
                    keyName="knowledge.x 前当天发货 & y 后次日发货"
                    params={{
                      x: <TransComponent content={picker} />,
                      y: <TransComponent content={picker} />,
                    }}
                  />
                </Inline>
              </Radio>
            );
          }}
        </FormField>
      )}
      <Radio
        className={singleMode ? hideRadioClassName : ''}
        checked={valueType === 'asSoon'}
        onChange={({ target: { checked } }) => checked && onValueTypeChange('asSoon')}
        style={getHideStyle('asSoon')}
      >
        {t('会尽快发货')}
      </Radio>
    </div>
  );
}

export interface MultipleShippingDaysFormValues {
  first: FV;
  preSale?: FV;
}

type MFV = MultipleShippingDaysFormValues;

export function MultipleShippingDays({
  prefix,
  singleMode,
  render,
}: {
  prefix?: Prefix;
  singleMode?: boolean;
  render?: (first: ReactNode, preSale: ReactNode) => JSX.Element;
}) {
  const { t } = useTranslate();

  const {
    field: { value: first, onChange: onFirstChange },
  } = useSmartField<MFV['first']>(getPrefixName('first', prefix));
  const {
    field: { value: preSale, onChange: onPreSaleChange },
  } = useSmartField<MFV['preSale']>(getPrefixName('preSale', prefix));

  const firstPrefix = Array.isArray(prefix)
    ? [...prefix, 'first']
    : typeof prefix === 'string'
    ? `${prefix}.first`
    : 'first';
  const preSalePrefix = Array.isArray(prefix)
    ? [...prefix, 'preSale']
    : typeof prefix === 'string'
    ? `${prefix}.preSale`
    : 'preSale';

  const firstShippingDays = <ShippingDays singleMode={singleMode} prefix={firstPrefix} />;
  const preSaleShippingDays = preSale && (
    <ShippingDays singleMode={singleMode} prefix={preSalePrefix} />
  );

  if (singleMode) {
    const firstNode =
      first.valueType === 'asSoon' ? (
        <Inline>
          {first.saleType === 'inSale' && <span>{t('现货')}</span>}
          {first.saleType === 'preSale' && <span>{t('预售')}</span>}
          <div style={{ margin: '6px 0' }}>{firstShippingDays}</div>
        </Inline>
      ) : (
        <Inline>
          {first.saleType === 'inSale' && <span className="!mt-[-24px]">{t('现货')}</span>}
          {first.saleType === 'preSale' && <span className="!mt-[-24px]">{t('预售')}</span>}
          {firstShippingDays}
        </Inline>
      );
    const preSaleNode =
      preSale &&
      (preSale.valueType === 'asSoon' ? (
        <Inline>
          <span>{t('预售')}</span>
          <div style={{ margin: '6px 0' }}>{preSaleShippingDays}</div>
        </Inline>
      ) : (
        <Inline>
          <span className="!mt-[-24px]">{t('预售')}</span>
          {preSaleShippingDays}
        </Inline>
      ));
    if (render) {
      return render(firstNode, preSaleNode);
    }
    return (
      <div>
        {firstNode}
        {preSaleNode}
      </div>
    );
  }

  return (
    <div>
      <Inline>
        <span>{t('区分现货/预售')}</span>
        <Switch
          checked={Boolean(preSale)}
          onChange={(checked) => {
            if (checked) {
              if (first.saleType === 'preSale') {
                onPreSaleChange({
                  ...first,
                  saleType: 'preSale',
                });
                onFirstChange({
                  ...defaultValues,
                  storeScopes: first.storeScopes,
                  saleType: 'inSale',
                });
              } else {
                onPreSaleChange({
                  ...defaultValues,
                  storeScopes: first.storeScopes,
                  valueType: 'range',
                  saleType: 'preSale',
                  range: {
                    rangeMin: 15,
                    rangeMax: 30,
                    unit: 'DAY',
                  },
                });
                onFirstChange({
                  ...first,
                  saleType: 'inSale',
                });
              }
            } else {
              onPreSaleChange(undefined);
              onFirstChange({
                ...first,
                saleType: 'all',
              });
            }
          }}
        />
      </Inline>
      {preSale && (
        <p style={{ fontSize: 12, color: 'var(--fett-text-tertiary)', margin: '8px 0' }}>
          {t(
            '小乐会自动检测商品属于现货还是预售，并依据商品当下所处的实际状态来发送对应的发货天数。\n',
          )}
        </p>
      )}
      <Divider style={{ margin: '16px 0' }} />
      <Form layout="horizontal">
        {first.saleType === 'inSale' && <Title>{t('现货')}</Title>}
        {first.saleType === 'preSale' && <Title>{t('预售')}</Title>}
        <Form.Item required label={t('请选择')} style={{ marginBottom: 0 }}>
          {firstShippingDays}
        </Form.Item>
        {preSale && [
          <Divider />,
          <Title>{t('预售')}</Title>,
          <Form.Item required label={t('请选择')} style={{ marginBottom: 0 }}>
            {preSaleShippingDays}
          </Form.Item>,
        ]}
      </Form>
    </div>
  );
}

const defaultValues = defaultValueMap.AI_MODEL_SHIPPING_DAYS;

export function ShippingDaysBootGuide(props: AiModelState<FV>) {
  return (
    <NormalMode
      state={props}
      tips={props.t(
        '让我们先来聊聊「发货与物流」吧您的店铺商品发货天数是多久呢?以下是一些常见的运输天数选项供您选择和调整',
      )}
      getFormContent={(prefix) => <ShippingDays prefix={prefix} />}
      getFirstInitialValues={useCallback(({ stores, defaultValueRule }) => {
        const crossStores = stores.filter(
          ({ storeType, is3pf }) => storeType === 'CROSS' && !is3pf,
        );
        const localStores = stores.filter(
          ({ storeType, is3pf }) => storeType === 'LOCAL' && !is3pf,
        );
        const pfStores = stores.filter(({ is3pf }) => is3pf);

        const {
          TODAY_OR_TOMORROW_ON_TIME: {
            todayOrTomorrowTime = defaultValues.todayOrTomorrowTime,
          } = {},
          RANGE: { crossStoreRange, localStoreRange, pfStoreRange } = {},
        } = defaultValueRule?.LOGISTICS_SEND_OUT_TIME || {};

        const values: FV[] = [];

        const rangeTransform = (
          range?: BootGuide.AiModel.DefaultValueRule['LOGISTICS_SEND_OUT_TIME']['RANGE']['crossStoreRange'],
        ) => ({
          rangeMin: range?.onSaleMin || defaultValues.range.rangeMin,
          rangeMax: range?.onSaleMax || defaultValues.range.rangeMax,
        });

        if (crossStores.length) {
          values.push({
            ...defaultValues,
            storeScopes: stores2storeScopes(crossStores),
            valueType: 'range',
            todayOrTomorrowTime,
            range: {
              ...defaultValues.range,
              ...rangeTransform(crossStoreRange),
            },
          });
        }
        if (localStores.length) {
          values.push({
            ...defaultValues,
            storeScopes: stores2storeScopes(localStores),
            valueType: 'range',
            todayOrTomorrowTime,
            range: {
              ...defaultValues.range,
              ...rangeTransform(localStoreRange),
            },
          });
        }
        if (pfStores.length) {
          values.push({
            ...defaultValues,
            storeScopes: stores2storeScopes(pfStores),
            valueType: 'range',
            todayOrTomorrowTime,
            range: {
              ...defaultValues.range,
              ...rangeTransform(pfStoreRange),
            },
          });
        }

        return { values };
      }, [])}
      updateApi={updateShippingDays}
      getRemainTips={(remainLength) =>
        props.t(
          '您有x家店铺还未配置，请选择店铺商品发货天数。如果您无法确认店铺商品发货天数，建议选择会尽快发货',
          { x: remainLength },
        )
      }
      descriptionRender={(value, storeMap) => (
        <StoreDescription storeScopes={value} storeMap={storeMap} />
      )}
    />
  );
}

export function StoreDescription({
  storeScopes,
  storeMap,
}: {
  storeScopes: General.StoreScope[];
  storeMap: AiModelState<FV>['storeMap'];
}) {
  const { t } = useTranslate();

  if (!storeScopes.length) {
    return <></>;
  }

  const judge = (judger: (store?: UnConfigStore) => any) => {
    return storeScopes.every(({ regionStoreScopes }) => {
      return regionStoreScopes.every(({ storeIds }) => {
        return storeIds?.every((id) => {
          return judger(storeMap[id]);
        });
      });
    });
  };

  if (judge((store) => store?.is3pf)) {
    return <Tag color="orange">{t('3PF店铺')}</Tag>;
  }

  if (judge((store) => store?.storeType === 'CROSS' && !store.is3pf)) {
    return <Tag color="blue">{t('跨境店铺')}</Tag>;
  }

  if (judge((store) => store?.storeType === 'LOCAL' && !store.is3pf)) {
    return <Tag color="green">{t('本土店铺')}</Tag>;
  }

  return <></>;
}

const Title = styled.h4`
  position: relative;
  text-indent: 12px;
  &::before {
    display: block;
    content: '';
    position: absolute;
    top: 6px;
    left: 0;
    width: 4px;
    height: 14px;
    background: var(--ant-primary-color);
  }
`;

export default ShippingDays;
